/**
 * Wes agent config store (agent_settings.agent_type = 'wes').
 */

import { create } from 'zustand';
import {
  getAgentSettings,
  upsertAgentSettings,
  type AgentSettings,
} from '../../lib/agentSettingsService';
import {
  defaultWesSystemPrompt,
  packWesSettings,
  unpackWesSettings,
  type WesVoiceMode,
} from '../../lib/wes/wesPrompts';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

export const WES_AGENT_TYPE = 'wes';

interface WesStoreState {
  config: AgentSettings | null;
  voiceMode: WesVoiceMode;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchConfig: (workspaceId: string) => Promise<void>;
  saveConfig: (
    workspaceId: string,
    updates: {
      enabled?: boolean;
      model?: string;
      voiceMode?: WesVoiceMode;
      promptBody?: string;
      autoRunOnCreate?: boolean;
    },
    updatedBy?: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

export const useWesStore = create<WesStoreState>((set, get) => ({
  config: null,
  voiceMode: 'normal',
  isLoading: false,
  isSaving: false,
  error: null,
  loadedWorkspaceId: null,

  fetchConfig: async (workspaceId) => {
    const existing = get();
    if (existing.loadedWorkspaceId === workspaceId && existing.config !== null) {
      return;
    }

    set({ isLoading: true, error: null });

    const { data, error } = await getAgentSettings(workspaceId, WES_AGENT_TYPE);
    if (error) {
      set({ isLoading: false, error });
      return;
    }

    const effective: AgentSettings = data ?? {
      workspaceId,
      agentType: WES_AGENT_TYPE,
      enabled: true,
      model: DEFAULT_MODEL,
      systemPrompt: packWesSettings('normal', defaultWesSystemPrompt('normal')),
      autoRunOnCreate: false,
      updatedAt: new Date().toISOString(),
    };

    const { voiceMode } = unpackWesSettings(effective.systemPrompt);

    set({
      config: effective,
      voiceMode,
      isLoading: false,
      loadedWorkspaceId: workspaceId,
    });
  },

  saveConfig: async (workspaceId, updates, updatedBy) => {
    const current = get().config;
    const voiceMode = updates.voiceMode ?? get().voiceMode;
    const base = current ?? {
      workspaceId,
      agentType: WES_AGENT_TYPE,
      enabled: true,
      model: DEFAULT_MODEL,
      systemPrompt: packWesSettings(voiceMode, defaultWesSystemPrompt(voiceMode)),
      autoRunOnCreate: false,
      updatedAt: new Date().toISOString(),
    };

    let promptBody = updates.promptBody;
    if (promptBody === undefined) {
      const unpacked = unpackWesSettings(base.systemPrompt);
      promptBody = unpacked.promptBody || defaultWesSystemPrompt(voiceMode);
    }

    const next = {
      enabled: updates.enabled ?? base.enabled,
      model: updates.model ?? base.model,
      systemPrompt: packWesSettings(voiceMode, promptBody),
      autoRunOnCreate: updates.autoRunOnCreate ?? base.autoRunOnCreate,
    };

    set({ isSaving: true, error: null });

    const { data, error } = await upsertAgentSettings({
      workspaceId,
      agentType: WES_AGENT_TYPE,
      ...next,
      updatedBy,
    });

    if (error || !data) {
      set({ isSaving: false, error: error ?? 'Save failed' });
      return { success: false, error: error ?? 'Save failed' };
    }

    set({
      config: data,
      voiceMode,
      isSaving: false,
    });
    return { success: true };
  },
}));
